import Form from '@components/Form'
import type { HelmCorridor, HelmHole } from '@features/fields/api/useHelmInspect'
import * as turf from '@turf/turf'
import type { Locutus } from 'api-validations/locutus'
import { BoundaryHoleBehavior, GuidancePrimitive } from 'api-validations/locutus/guidance'
import type { Feature, LineString, Polygon } from 'geojson'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import { CircleSpinner, Icon } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { useMapContext } from 'shared-ui/map'

import type { FormValues } from './formtypes'
import { corridorKey, holeKey } from './overrideKeys'

type QueryLike = {
  isLoading: boolean
  isError: boolean
  error?: { message?: string } | null
}

// Wraps loading/error/empty placeholders for helm-inspect-backed panels so
// each override page renders identical scaffolding without duplicating the
// three guard branches.
export const InspectStates = ({
  query,
  isEmpty,
  emptyText,
  children
}: {
  query: QueryLike
  isEmpty: boolean
  emptyText: string
  children: ReactNode
}) => {
  const { t } = useLocalization()
  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <CircleSpinner />
      </div>
    )
  }
  if (query.isError) {
    return (
      <p className="px-1 py-4 text-sm text-red-600">
        {query.error?.message ?? t(($) => $.messages.errorOccurredTitle)}
      </p>
    )
  }
  if (isEmpty) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <Icon icon={SpriteMap.GuidanceBoundaryIcon} className="size-8 text-gray-300" />
        <p className="text-sm text-gray-500">{emptyText}</p>
      </div>
    )
  }
  return <>{children}</>
}

// A per-edge headland count. An edge without an override shows the global
// count as its value (greyed) rather than as a placeholder, so the spinner and
// arrow keys step from the inherited count instead of from an empty field.
// `valueAsNumber: true` on the global input gives NaN while it is empty.
export const HeadlandCountInput = ({
  count,
  globalCount,
  className,
  onChange
}: {
  count: number | undefined
  globalCount: number
  className: string
  onChange: (raw: string) => void
}) => {
  const [draft, setDraft] = useState<string | null>(null)
  const inherited = count === undefined || !Number.isFinite(count)
  const base = Number.isFinite(globalCount) ? String(globalCount) : ''
  const value = draft ?? (inherited ? base : String(count))
  return (
    <Form.Input
      type="number"
      min={0}
      className={`${className} ${inherited && draft === null ? 'text-gray-400' : ''}`}
      value={value}
      placeholder={base}
      onFocus={(e) => e.target.select()}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        setDraft(e.target.value)
        onChange(e.target.value)
      }}
    />
  )
}

const overrideRowClasses = createVariants({
  base: 'flex flex-col gap-2 rounded-md border bg-white px-3 py-2',
  variants: {
    selected: { true: 'border-gray-800', false: 'border-gray-100' }
  }
})

const pillSegmentClasses = createVariants({
  base: 'flex flex-1 items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400',
  variants: {
    selected: { false: 'text-gray-500 hover:bg-gray-100' }
  }
})

// Shared row layout for override lists (headlands/corners/holes). When
// `onSelect` is supplied the row behaves as a clickable button and paints a
// selected border+tint using `color`.
export const OverrideRow = ({
  color,
  label,
  isOverridden,
  onReset,
  resetLabel,
  isSelected = false,
  onSelect,
  children
}: {
  color: string
  label: string
  isOverridden: boolean
  onReset: () => void
  // Defaults to "reset to global"; rows whose reset means something else
  // (a corridor's "keep separate") name it.
  resetLabel?: string
  isSelected?: boolean
  onSelect?: () => void
  children: ReactNode
}) => {
  const { t } = useLocalization()
  const clickable = onSelect !== undefined
  const labelContent = (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="truncate text-sm text-gray-700">{label}</span>
    </div>
  )
  return (
    <div
      className={overrideRowClasses({ selected: isSelected === true })}
      style={isSelected ? { backgroundColor: `${color}22` } : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        {clickable ? (
          <button
            type="button"
            onClick={onSelect}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
          >
            {labelContent}
          </button>
        ) : (
          labelContent
        )}
        {isOverridden && (
          <button
            type="button"
            onClick={onReset}
            className="shrink-0 text-xs text-gray-400 underline hover:text-gray-600"
          >
            {resetLabel ?? t(($) => $.planning.resetToGlobal)}
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

export const PillToggle = <T,>({
  value,
  options,
  onChange,
  inherited = false
}: {
  value: T
  options: readonly [PillOption<T>, PillOption<T>]
  onChange: (v: T) => void
  inherited?: boolean
}) => {
  const current = options.find((o) => o.value === value)
  const colorClass = current ? (inherited ? current.subtleColor : current.strongColor) : ''
  return (
    <div className="flex shrink-0 rounded-full border border-gray-300 bg-white p-0.5">
      {options.map((opt) => {
        const selected = opt.value === value
        return (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
            className={pillSegmentClasses({
              selected,
              className: selected ? colorClass : undefined
            })}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export type PillOption<T> = {
  value: T
  label: string
  subtleColor: string
  strongColor: string
}

// The draw tool's `addGuidancePrimitive` expects a LineString. AHeading and
// RadialPivot are persisted as Point + `headingDeg`/`radiusFt`, so we
// synthesize a two-point LineString (center → destination at the appropriate
// bearing/distance). decorateGuidanceFeature re-collapses AHeading back to a
// Point on insert; RadialPivot stays as a LineString whose endpoints encode
// the radius. Returns null for primitives we don't know how to hydrate.
export const savedPrimitiveToLine = (
  data: Locutus.GuidancePrimitiveAsset['data']
): Feature<LineString> | null => {
  if (data.type === GuidancePrimitive.AHeading) {
    const { primitive } = data
    if (primitive.geometry.type !== 'Point') return null
    const center = primitive.geometry.coordinates as [number, number]
    // Length is only cosmetic — decorateGuidanceFeature reads the bearing off
    // the two points and immediately collapses back to a Point. 100 ft keeps
    // it clear of floating-point noise near the anchor.
    const end = turf.destination(
      turf.point(center),
      100,
      primitive.properties.guidance.headingDeg,
      { units: 'feet' }
    ).geometry.coordinates as [number, number]
    return {
      type: 'Feature',
      id: primitive.id,
      geometry: { type: 'LineString', coordinates: [center, end] },
      properties: {}
    }
  }
  if (data.type === GuidancePrimitive.RadialPivot) {
    const { primitive } = data
    if (primitive.geometry.type !== 'Point') return null
    const center = primitive.geometry.coordinates as [number, number]
    // Bearing is arbitrary — the RadialPivot style renders a circle around A;
    // only |A→B| matters.
    const end = turf.destination(turf.point(center), primitive.properties.guidance.radiusFt, 0, {
      units: 'feet'
    }).geometry.coordinates as [number, number]
    return {
      type: 'Feature',
      id: primitive.id,
      geometry: { type: 'LineString', coordinates: [center, end] },
      properties: {}
    }
  }
  const { primitive } = data
  if (primitive.geometry.type !== 'LineString') return null
  return {
    type: 'Feature',
    id: primitive.id,
    geometry: primitive.geometry,
    properties: {}
  }
}

// A hole is passable when its per-hole override says so, or when it has no
// override and the plan-level default is passable.
export const usePassableHolePolygons = (
  form: UseFormReturn<FormValues>,
  holes: HelmHole[]
): Feature<Polygon>[] => {
  const globalBehavior = useWatch({ control: form.control, name: 'boundaryHoleBehavior' })
  const holeOverrides = useWatch({ control: form.control, name: 'holeOverrides' }) ?? []
  return useMemo(() => {
    const globalPassable = globalBehavior === BoundaryHoleBehavior.Passable
    return holes
      .filter((hole) => {
        const key = holeKey(hole)
        return holeOverrides.find((o) => o.key === key)?.passable ?? globalPassable
      })
      .map((h) => h.feature)
  }, [holes, holeOverrides, globalBehavior])
}

// Edge ids that stop being boundary sides once a corridor is overridden.
// Helm melds the two regions across the strip, so the banks (edgesA/edgesB)
// become interior to the field for a passable corridor, or sides of the
// strip's hole ring for an impassable one; either way their per-side
// headland overrides no longer apply and the map should not offer them.
export const useCorridorBankEdgeIds = (
  form: UseFormReturn<FormValues>,
  corridors: HelmCorridor[]
): Set<string> => {
  const corridorOverrides = useWatch({ control: form.control, name: 'corridorOverrides' }) ?? []
  return useMemo(() => {
    const set = new Set<string>()
    for (const corridor of corridors) {
      const key = corridorKey(corridor)
      if (!corridorOverrides.some((o) => o.key === key)) continue
      for (const id of corridor.edgesA) set.add(id)
      for (const id of corridor.edgesB) set.add(id)
    }
    return set
  }, [corridors, corridorOverrides])
}

// Pushes helm-inspect features onto the view-only draw layer under `kind`.
// The DrawToolManager only paints them when the matching scene is active,
// so callers can write once and let scene transitions govern visibility.
export const useViewOnlyFeatures = <T extends { feature: Feature }>(
  kind: string,
  items: T[],
  extraProps: (item: T, index: number) => Record<string, unknown> = () => ({})
) => {
  const { map } = useMapContext()
  useEffect(() => {
    map.drawTool.setViewOnlyFeaturesByKind(
      kind,
      items.map((item, i) => ({
        ...item.feature,
        properties: {
          ...item.feature.properties,
          kind,
          ...extraProps(item, i)
        }
      }))
    )
    return () => {
      map.drawTool.clearViewOnlyByKind(kind)
    }
  }, [map, items]) // eslint-disable-line react-hooks/exhaustive-deps
}
